// src/pages/LegacyDistrictPage.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { detectParentInfo } from '../utils/parentOrigin';
import { VideoPopupProvider, useVideoPopup } from '../components/VideoPopupContext';

import Meetings from '../components/Meetings';
import Topics from '../components/Topics';
import VotingAnalysis from '../components/VotingAnalysis';
import SearchBar from '../components/SearchBar';
import SearchResults from './SearchResults';
import Chatbot from '../components/Chatbot';
import WidgetAccessDenied from '../components/WidgetAccessDenied';
import VimeoPlayerEmbed from '../components/VimeoPlayerEmbed';

import summariesIcon from '../assets/summaries.png';
import topicsIcon from '../assets/topics.png';
import electionsIcon from '../assets/elections.png'; // 1. Uncommented import
import searchIcon from '../assets/search.png';

import './LegacyDistrictPage.css';

/* --- Video Modal Component (Floating + Expandable + Draggable) --- */
const VideoModal = () => {
  const { videoState, closeVideo } = useVideoPopup();
  const [isExpanded, setIsExpanded] = useState(false); // Default: Small
  
  // -- Draggable State --
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Drag offset
  const dragStartRef = useRef({ x: 0, y: 0 }); // Where mouse was clicked
  const positionStartRef = useRef({ x: 0, y: 0 }); // Where modal was before drag

  // Reset drag offset when the video source/timestamp changes (so it starts at the correct auto-scroll position)
  useEffect(() => {
    if (videoState) {
        setPosition({ x: 0, y: 0 });
    }
  }, [videoState]); 

  // -- Drag Handlers --
  const handleMouseDown = (e) => {
    // Only drag if clicking the header itself, not buttons
    if (e.target.closest('button')) return;
    
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { ...position };
    e.preventDefault(); // Prevent text selection
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      
      setPosition({
        x: positionStartRef.current.x + dx,
        y: positionStartRef.current.y + dy
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]); 

  if (!videoState) return null;

  // -- Dynamic Styling --
  const overlayStyle = {
      transform: `translate(${position.x}px, ${position.y}px)` // Apply drag offset
  };

  // Apply initial API-based positioning (next to timestamp)
  if (videoState.position && videoState.position.top !== undefined) {
      overlayStyle.top = `${videoState.position.top}px`;
      overlayStyle.bottom = 'auto'; 
  }

  return (
    <div 
        className={`legacy-video-overlay ${isExpanded ? 'expanded' : ''}`}
        style={overlayStyle}
    >
      <div className="legacy-video-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header is the Drag Handle */}
        <div 
            className="legacy-video-header" 
            onMouseDown={handleMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
           <span className="legacy-video-title">Video Player</span>
           <div className="legacy-video-controls">
             {videoState.externalUrl && (
               <a
                 className="legacy-control-btn legacy-external-link"
                 href={videoState.externalUrl}
                 target="_blank"
                 rel="noopener noreferrer"
                 onMouseDown={(e) => e.stopPropagation()}
                 title={
                   videoState.platform === 'vimeo'
                     ? "Open on Vimeo (use this if the embed says the video can't be played here)"
                     : 'Open in a new tab'
                 }
               >
                 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                   <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                   <polyline points="15 3 21 3 21 9"></polyline>
                   <line x1="10" y1="14" x2="21" y2="3"></line>
                 </svg>
               </a>
             )}
             <button
               className="legacy-control-btn expand-btn"
               onClick={() => setIsExpanded(!isExpanded)}
               title={isExpanded ? "Minimize" : "Expand"}
             >
               {isExpanded ? (
                 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                   <polyline points="4 14 10 14 10 20"></polyline>
                   <polyline points="20 10 14 10 14 4"></polyline>
                 </svg>
               ) : (
                 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                   <polyline points="15 3 21 3 21 9"></polyline>
                   <polyline points="9 21 3 21 3 15"></polyline>
                   <line x1="21" y1="3" x2="14" y2="10"></line>
                   <line x1="3" y1="21" x2="10" y2="14"></line>
                 </svg>
               )}
             </button>
             <button className="legacy-control-btn close-btn" onClick={closeVideo}>&times;</button>
           </div>
        </div>
        
        <div className="legacy-video-wrapper">
          {isDragging && <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'transparent' }} />}

          {videoState.platform === 'vimeo' ? (
            <VimeoPlayerEmbed
              src={videoState.src}
              title="Meeting Video"
              externalUrl={videoState.externalUrl}
            />
          ) : (
            <iframe
              src={videoState.src}
              title="Meeting Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>
  );
};

function LegacyDistrictPage({ isWidget = false }) {
  const { abbreviation } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const boardRef = useRef(null);
  const containerRef = useRef(null);

  /* ───────── State ───────── */
  const [activeTab, setActiveTab] = useState('Meetings');
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search
  const [results, setResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLimit, setSearchLimit] = useState(15);
  // Tracks the latest in-flight search so older racing responses can't clobber it.
  const searchRequestIdRef = useRef(0);
  // AbortController for the current in-flight fetch.
  const searchAbortRef = useRef(null);
  const lastTypedTermRef = useRef('');

  // Widget Security
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Chatbot State
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Browser-derived parent info (ancestorOrigins / document.referrer).
  // Cannot be spoofed by a fake ?parentUrl query param.
  const parentInfo = useMemo(() => detectParentInfo(), []);

  /* ───────── 1. Seamless Resize Logic ───────── */
  useEffect(() => {
    if (!isWidget) return;
    const sendHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({ type: 'resize', height }, '*');
      }
    };
    const observer = new ResizeObserver(sendHeight);
    if (containerRef.current) observer.observe(containerRef.current);
    sendHeight();
    return () => observer.disconnect();
  }, [isWidget, activeTab, results, loading, searchLoading, isChatOpen]);

  /* ───────── 2. Sync Tab & Scroll ───────── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab')?.toLowerCase();
    const hash = location.hash;

    let currentTab = 'Meetings';
    switch (tabParam) {
      case 'search': currentTab = 'Search'; break;
      case 'topics': currentTab = 'Topics'; break;
      case 'voting':
      case 'votinganalysis': currentTab = 'Voting'; break;
      default: currentTab = 'Meetings';
    }

    setActiveTab(currentTab);

    if (hash && hash.startsWith('#meeting-')) {
      setActiveTab('Meetings');
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [location]);

  /* ───────── 3. Fetch district meta ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      if (isWidget) {
        try {
          const res = await fetch('/.netlify/functions/districtData', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resource: 'district',
              abbreviation,
              parentOrigin: parentInfo.origin,
              parentUrl: parentInfo.url,
            }),
          });

          if (res.status === 403) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload.error || 'Failed to load district');
          }
          const payload = await res.json();
          setDistrict(payload.district);
          setLoading(false);
          return;
        } catch (err) {
          setError(err);
          setLoading(false);
          return;
        }
      }

      const { data, error } = await supabase
        .from('Districts')
        .select('*')
        .eq('abbreviation', abbreviation)
        .single();

      if (error) setError(error);
      else setDistrict(data);

      setLoading(false);
    })();
  }, [abbreviation, isWidget, parentInfo]);

  // No generic photo fallback: districts without a real image (no image_url,
  // no assets/{id}.png) render text-only rather than borrowing another
  // district's photo.
  const districtImage = () => {
    if (!district) return null;
    if (district.image_url) return district.image_url; // config-driven image (e.g. cities)
    try { return require(`../assets/${district.id}.png`); }
    catch { return null; }
  };

  // Transcript proxy config (was hardcoded pausd/suhsd in TranscriptWindow).
  const useProxy = !!(district?.is_public || district?.is_first_party);

  const chooseTab = (tab) => {
    const tabSlug = tab.toLowerCase();
    setActiveTab(tab);
    
    const params = new URLSearchParams(location.search);
    params.set('tab', tabSlug);
    navigate(`?${params.toString()}`);
    if (window.innerWidth < 768 && boardRef.current) {
      boardRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /* ───────── SEARCH LOGIC ───────── */
  const generateSmartTitle = (chunk, searchTerm) => {
    if (chunk.agenda_item) return chunk.agenda_item;
    const text = chunk.content || chunk.text || '';
    const timeMatch = text.match(/\[(\d{1,2}:\d{2}(?::\d{2})?)\]/);
    if (timeMatch) return `Transcript at ${timeMatch[1]}`;
    return `Mention of "${searchTerm}"`;
  };

  const executeSearch = useCallback(
    async (term, scope = 'all', limit = 15, startDate = null, endDate = null, loose = false) => {
      if (!term.trim()) return;
      if (!district?.id) return;

      // Abort any prior in-flight fetch and mark this as the latest request.
      if (searchAbortRef.current) {
        try { searchAbortRef.current.abort(); } catch (e) {}
      }
      const controller = new AbortController();
      searchAbortRef.current = controller;
      const myRequestId = ++searchRequestIdRef.current;
      if (limit === 15) setSearchLoading(true);

      try {
        const response = await fetch('/.netlify/functions/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            term, scope, maxResults: limit, districtId: district.id, abbreviation,
            parentOrigin: parentInfo.origin,
            parentUrl: parentInfo.url,
            startDate: startDate || null, endDate: endDate || null,
            loose: Boolean(loose)
          }),
          signal: controller.signal,
        });

        if (myRequestId !== searchRequestIdRef.current) return; // stale
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        if (myRequestId !== searchRequestIdRef.current) return; // stale

        const isArrayResponse = Array.isArray(data);
        const meetingItemsRaw = isArrayResponse ? data : data.meeting_items || [];
        const transcriptRaw = isArrayResponse ? [] : data.transcript_matches || [];
        const looseAvailable = !isArrayResponse && data.loose_available === true;

        const meetingResults = (meetingItemsRaw || []).map((item) => ({
          ...item, type: item.type || 'discussion', content: item.content || item.summary || '',
        }));

        let transcriptResults = [];
        if (transcriptRaw.length > 0) {
          const meetingIds = [...new Set(transcriptRaw.map((t) => t.meeting_id).filter(Boolean))];
          let videoMap = {};
          if (meetingIds.length > 0) {
            const { data: videoData } = await supabase.from('Meetings').select('id, youtube_link').in('id', meetingIds);
            if (videoData) videoData.forEach((row) => { videoMap[row.id] = row.youtube_link; });
          }
          if (myRequestId !== searchRequestIdRef.current) return; // stale
          transcriptResults = transcriptRaw.map((chunk) => ({
            ...chunk,
            title: generateSmartTitle(chunk, term),
            type: 'discussion',
            content: chunk.content || chunk.text || '',
            date: chunk.date,
            video_url: videoMap[chunk.meeting_id] || null,
          }));
        }

        setResults({
            summaries: [...meetingResults, ...transcriptResults],
            topics: [],
            term, scope, startDate, endDate,
            loose: Boolean(loose),
            looseAvailable: looseAvailable
        });
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.error('Search failed:', err);
        if (myRequestId === searchRequestIdRef.current) {
          setResults(null);
        }
      } finally {
        if (myRequestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    },
    [abbreviation, district, parentInfo]
  );

  const handleSearchTermChange = useCallback((nextTerm) => {
    const trimmed = (nextTerm || '').trim();
    const prev = lastTypedTermRef.current;
    lastTypedTermRef.current = trimmed;
    if (trimmed === prev) return;

    ++searchRequestIdRef.current;
    if (searchAbortRef.current) {
      try { searchAbortRef.current.abort(); } catch (e) {}
      searchAbortRef.current = null;
    }
    setResults(null);
    setSearchLoading(Boolean(trimmed));
  }, []);

  const handleSearch = useCallback((term, scope = 'all', startDate = null, endDate = null) => {
      setSearchLimit(15);
      setResults(null);
      executeSearch(term, scope, 15, startDate, endDate);
  }, [executeSearch]);

  const handleLoadMore = () => {
    if (!results?.term) return;
    const newLimit = searchLimit + 15;
    setSearchLimit(newLimit);

    const isCurrentlyLoose = Boolean(results.loose);
    const exhaustedTight = (results.summaries?.length || 0) < searchLimit;
    const useLoose = isCurrentlyLoose || (exhaustedTight && results.looseAvailable === true);

    executeSearch(results.term, results.scope || 'all', newLimit, results.startDate, results.endDate, useLoose);
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'Search':
        return (
          <div className="search-tab-container">
            <div className="search-card">
              <div className="search-card-header">
                <h3>Search Board Intelligence</h3>
                <p>Search across meeting summaries and raw transcripts.</p>
                <SearchBar onSearch={handleSearch} onTermChange={handleSearchTermChange} isSearching={searchLoading} />
              </div>
              <div className="search-card-results">
                <SearchResults
                  results={results}
                  isLoading={searchLoading}
                  onLoadMore={handleLoadMore}
                  hasMore={Boolean(
                    (results?.summaries?.length || 0) >= searchLimit
                    || (!results?.loose && results?.looseAvailable === true)
                  )}
                  onClearSearch={() => { setResults(null); }}
                  isWidget={isWidget}
                  parentInfo={parentInfo}
                  useProxy={useProxy}
                />
              </div>
            </div>
          </div>
        );
      case 'Meetings': return <Meetings isWidget={isWidget} parentInfo={parentInfo} isLegacy={true} />;
      case 'Topics': return <Topics isWidget={isWidget} parentInfo={parentInfo} />;
      case 'Voting': return <VotingAnalysis />;
      default: return null;
    }
  };

  if (loading) return <p>Loading district info…</p>;

  if (accessDenied) {
    return (
      <div ref={containerRef}>
        <WidgetAccessDenied parentInfo={parentInfo} />
      </div>
    );
  }

  if (error) return <p>Error: {error.message}</p>;

  // 2. Updated Tabs Logic
  // Voting sits below Topics for every district. Districts without voting
  // data render the component's empty "No votes" state.
  const sidebarTabs = [
    ['Search', searchIcon],
    ['Meetings', summariesIcon],
    ['Topics', topicsIcon],
    ['Voting', electionsIcon],
  ];

  const headerImage = districtImage();

  return (
    <VideoPopupProvider>
      <VideoModal />

      <div className={`district-page ${isWidget ? 'widget-mode' : ''}`} ref={containerRef}>
        {!isWidget && (
          <div className="district-header">
            {headerImage && (
              <div className="district-image"><img src={headerImage} alt={district.name} /></div>
            )}
            <div className="district-info">
              <h2>{district.name}</h2>
              <p>Address: {district.address}</p>
              {district.entity_type !== 'city_council' && (
                <p>Enrollment: {district.totalStudents ? new Intl.NumberFormat().format(district.totalStudents) : 'N/A'}</p>
              )}
              <p>Phone: {district.phone}</p>
            </div>
          </div>
        )}

        <div className="district-content">
          <aside className="district-sidebar">
            <div className="board-breakdown-container" ref={boardRef}>
              <h3 className="sidebar-title">Board Breakdown</h3>
              {sidebarTabs.map(([key, icon]) => (
                <button key={key} onClick={() => chooseTab(key)} className={activeTab === key ? 'active' : ''}>
                  {key}
                  <img src={icon} alt={key} className="button-icon" />
                </button>
              ))}
            </div>
          </aside>
          <div className="content-area">{renderTab()}</div>
        </div>

        <p className="page-copyright">
          © {new Date().getFullYear()} BoardWatch. All rights reserved.
        </p>

        {/* ───────── PREMIUM CHATBOT INTEGRATION ───────── */}
        {district?.id && district?.premium_chat_enabled && (
            <div className={`chat-widget-container ${isChatOpen ? 'open' : ''}`}>
                {isChatOpen && (
                    <Chatbot 
                        districtId={district.id} 
                        onClose={() => setIsChatOpen(false)} 
                    />
                )}
                <button 
                    className="chat-launcher" 
                    onClick={() => setIsChatOpen(!isChatOpen)}
                    aria-label="Toggle Chat"
                >
                    {isChatOpen ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="launcher-icon">
                            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor" className="launcher-icon">
                           <path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4 7.2-6-4.8h7.6z" />
                        </svg>
                    )}
                </button>
            </div>
        )}
      </div>
    </VideoPopupProvider>
  );
}

export default LegacyDistrictPage;